import { GAME_VERSION } from '../config.ts';
import type { Dir, SimEvent } from '../sim/index.ts';
import type { FireVariant, Settings } from '../settings.ts';

export interface MissedTurnEvent {
  t: number; // ms since session start
  boardId: string;
  level: number;
  tick: number;
  dir: Dir;
  reason: string;
  boundariesWaited: number;
}
export interface AccidentalFireEvent {
  t: number;
  boardId: string;
  level: number;
  tick: number;
  reason: string;
  variant: FireVariant;
}
export interface LevelRecord {
  boardId: string;
  attempts: number;
  passed: boolean;
  timeToPassMs: number | null;
  timeToAbandonMs: number | null;
  abandons: number;
  deaths: number;
}
export interface RunRecord {
  mode: 'campaign' | 'gauntlet';
  seed: number;
  score: number;
  ticks: number;
  outcome: string;
  levelsCleared: number;
}
export interface RestartLatency {
  t: number;
  kind: 'death' | 'timeUp';
  /** ms from death (or the time-up restart tap) to the first playable sim tick. */
  ms: number;
  /** ms from the skip input (pointerdown / key) to the first playable tick; null when the pause ran out on its own. */
  inputMs: number | null;
  via: 'pointerdown' | 'key' | 'auto';
}
/** WP6: power-up usage by source, beams, dry clicks, lantern fill times (Systems Brief §8). */
export interface KitUse {
  t: number;
  kind: string;
  source: 'pick' | 'pickup';
  boardId: string;
}

export interface Session {
  sessionId: string;
  appVersion: string;
  startedAt: string;
  endedAt: string | null;
  sessionLengthMs: number;
  device: { userAgent: string; width: number; height: number; dpr: number; touch: boolean };
  settings: Settings;
  /** The six acceptance-test fields. */
  attemptsPerLevel: Record<string, number>;
  timeToPass: Record<string, number | null>;
  timeToAbandon: Record<string, number | null>;
  missedTurnEvents: MissedTurnEvent[];
  accidentalFireEvents: AccidentalFireEvent[];
  /** Supporting counters. */
  levels: Record<string, LevelRecord>;
  turnsQueued: number;
  turnsExecuted: number;
  fires: number;
  firesBlocked: number;
  deaths: number;
  runs: RunRecord[];
  /** J3: restart latency samples. */
  restartLatencies: RestartLatency[];
  /** WP6 kit and lantern (Systems Brief §8). */
  kitUses: KitUse[];
  beams: number;
  dryClicks: number;
  /** ms from an empty lantern to full, per fill. */
  lanternFillMs: number[];
  /** Bible §3 A/B: the fire variant in use (last set) and per-variant counters. */
  fireVariant: FireVariant;
  fireVariantChanges: { t: number; variant: FireVariant }[];
  accidentalFiresByVariant: Record<FireVariant, number>;
  firesByVariant: Record<FireVariant, number>;
  /** ms from first entering a level to the first flame fired on it. */
  timeToFirstFire: Record<string, number | null>;
  /** Build stamp (git sha + time) of the page that produced this session. */
  build: string;
  /** Real-art load result at boot. */
  artLoad: { requested: number; loaded: number; failed: string[]; firstError: string | null; ms: number; source: string } | null;
}

export const TELEMETRY_KEY = 'proto.telemetry.v1';
const STORAGE_KEY = TELEMETRY_KEY;

function uid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export class Telemetry {
  readonly session: Session;
  private readonly t0 = performance.now();
  private levelEnteredAt = new Map<string, number>();
  private current: { boardId: string; level: number; tick: number } | null = null;
  private lanternEmptyAt: number | null = null;

  constructor(settings: Settings) {
    this.session = {
      sessionId: uid(),
      appVersion: GAME_VERSION,
      startedAt: new Date().toISOString(),
      endedAt: null,
      sessionLengthMs: 0,
      device: {
        userAgent: navigator.userAgent,
        width: window.innerWidth,
        height: window.innerHeight,
        dpr: window.devicePixelRatio || 1,
        touch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      },
      settings: { ...settings },
      attemptsPerLevel: {},
      timeToPass: {},
      timeToAbandon: {},
      missedTurnEvents: [],
      accidentalFireEvents: [],
      levels: {},
      turnsQueued: 0,
      turnsExecuted: 0,
      fires: 0,
      firesBlocked: 0,
      deaths: 0,
      runs: [],
      restartLatencies: [],
      kitUses: [],
      beams: 0,
      dryClicks: 0,
      lanternFillMs: [],
      fireVariant: settings.fireVariant,
      fireVariantChanges: [],
      accidentalFiresByVariant: { lantern: 0, doubleTap: 0 },
      firesByVariant: { lantern: 0, doubleTap: 0 },
      timeToFirstFire: {},
      build: 'unknown',
      artLoad: null,
    };
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush();
    });
    window.addEventListener('pagehide', () => this.flush());
    this.flush();
  }

  private now(): number {
    return performance.now() - this.t0;
  }

  private level(boardId: string): LevelRecord {
    let l = this.session.levels[boardId];
    if (!l) {
      l = { boardId, attempts: 0, passed: false, timeToPassMs: null, timeToAbandonMs: null, abandons: 0, deaths: 0 };
      this.session.levels[boardId] = l;
    }
    return l;
  }

  updateSettings(s: Settings): void {
    this.session.settings = { ...s };
    if (s.fireVariant !== this.session.fireVariant) {
      this.session.fireVariant = s.fireVariant;
      this.session.fireVariantChanges.push({ t: this.now(), variant: s.fireVariant });
    }
    this.flush();
  }

  /** Called when a level starts (fresh) — counts as an attempt. */
  levelStart(boardId: string, level: number): void {
    this.current = { boardId, level, tick: 0 };
    if (!this.levelEnteredAt.has(boardId)) this.levelEnteredAt.set(boardId, this.now());
    if (this.session.timeToFirstFire[boardId] === undefined) this.session.timeToFirstFire[boardId] = null;
    this.attempt(boardId);
  }

  private attempt(boardId: string): void {
    const l = this.level(boardId);
    l.attempts++;
    this.session.attemptsPerLevel[boardId] = l.attempts;
    this.flush();
  }

  setTick(tick: number): void {
    if (this.current) this.current.tick = tick;
  }

  /** Feed every sim event here. */
  onSimEvent(ev: SimEvent, boardId: string, level: number, tick: number): void {
    const s = this.session;
    switch (ev.type) {
      case 'turnQueued':
        s.turnsQueued++;
        break;
      case 'turnExecuted':
        s.turnsExecuted++;
        break;
      case 'turnMissed':
        s.missedTurnEvents.push({ t: this.now(), boardId, level, tick, dir: ev.dir, reason: ev.reason, boundariesWaited: ev.boundariesWaited });
        break;
      case 'fire':
        s.fires++;
        s.firesByVariant[s.fireVariant]++;
        if (s.timeToFirstFire[boardId] === undefined || s.timeToFirstFire[boardId] === null) {
          s.timeToFirstFire[boardId] = Math.round(this.now() - (this.levelEnteredAt.get(boardId) ?? this.now()));
        }
        break;
      case 'fireBlocked':
        s.firesBlocked++;
        if (ev.reason === 'dry') s.dryClicks++;
        break;
      case 'beam':
        s.beams++;
        break;
      case 'kitApplied':
        s.kitUses.push({ t: this.now(), kind: ev.kind, source: ev.source, boardId });
        break;
      case 'lanternFill':
        if (ev.fill === 1) this.lanternEmptyAt = this.now();
        if (ev.full && this.lanternEmptyAt !== null) {
          s.lanternFillMs.push(Math.round(this.now() - this.lanternEmptyAt));
          this.lanternEmptyAt = null;
        }
        break;
      case 'playerDeath':
        s.deaths++;
        this.level(boardId).deaths++;
        break;
      case 'respawn':
        this.attempt(boardId);
        break;
      case 'levelClear': {
        const l = this.level(boardId);
        if (!l.passed) {
          l.passed = true;
          l.timeToPassMs = this.now() - (this.levelEnteredAt.get(boardId) ?? this.now());
          s.timeToPass[boardId] = l.timeToPassMs;
        }
        this.current = null;
        this.flush();
        break;
      }
      case 'levelStart':
        this.levelStart(ev.boardId, ev.level);
        break;
      default:
        break;
    }
  }

  /** Player left a level without passing it (quit, game over, time up, tab hidden). */
  levelAbandon(reason: string): void {
    if (!this.current) return;
    const { boardId } = this.current;
    const l = this.level(boardId);
    if (!l.passed) {
      l.abandons++;
      l.timeToAbandonMs = this.now() - (this.levelEnteredAt.get(boardId) ?? this.now());
      this.session.timeToAbandon[boardId] = l.timeToAbandonMs;
    }
    this.current = null;
    void reason;
    this.flush();
  }

  accidentalFire(reason: string): void {
    const c = this.current ?? { boardId: '-', level: 0, tick: 0 };
    this.session.accidentalFireEvents.push({ t: this.now(), boardId: c.boardId, level: c.level, tick: c.tick, reason, variant: this.session.fireVariant });
    this.session.accidentalFiresByVariant[this.session.fireVariant]++;
    this.flush();
  }

  artLoad(r: { requested: number; loaded: number; failed: string[]; firstError: string | null; ms: number; source: string }, build: string): void {
    this.session.build = build;
    this.session.artLoad = { ...r, failed: r.failed.slice(0, 20) };
    this.flush();
  }

  restartLatency(r: { kind: 'death' | 'timeUp'; ms: number; inputMs: number | null; via: 'pointerdown' | 'key' | 'auto' }): void {
    this.session.restartLatencies.push({ t: this.now(), kind: r.kind, ms: Math.round(r.ms), inputMs: r.inputMs === null ? null : Math.round(r.inputMs), via: r.via });
    this.flush();
  }

  runEnd(r: RunRecord): void {
    this.session.runs.push(r);
    this.flush();
  }

  flush(): void {
    this.session.sessionLengthMs = Math.round(this.now());
    this.session.endedAt = new Date().toISOString();
    try {
      const all = loadStoredSessions().filter((s) => s.sessionId !== this.session.sessionId);
      all.push(this.session);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all.slice(-50)));
    } catch {
      /* storage may be unavailable */
    }
  }

  /** Full export: this session plus all stored prior sessions. */
  exportJson(): string {
    this.flush();
    return JSON.stringify({ exportedAt: new Date().toISOString(), appVersion: GAME_VERSION, sessions: loadStoredSessions() }, null, 2);
  }

  summary(): string {
    const s = this.session;
    const missRate = s.turnsQueued ? ((s.missedTurnEvents.length / s.turnsQueued) * 100).toFixed(1) : '–';
    const lines = [
      `Session ${s.sessionId} · ${(s.sessionLengthMs / 1000).toFixed(0)}s · build ${s.build}`,
      s.artLoad ? `Art: ${s.artLoad.loaded}/${s.artLoad.requested} loaded from ${s.artLoad.source} in ${s.artLoad.ms} ms${s.artLoad.firstError ? ` · first error: ${s.artLoad.firstError}` : ''}` : 'Art: no report',
      `Turns queued ${s.turnsQueued} · executed ${s.turnsExecuted} · missed ${s.missedTurnEvents.length} (${missRate}%)`,
      `Fires ${s.fires} · accidental ${s.accidentalFireEvents.length}`,
      `Fire variant ${s.fireVariant} · accidental lantern ${s.accidentalFiresByVariant.lantern} / double-tap ${s.accidentalFiresByVariant.doubleTap}`,
      ...Object.entries(s.timeToFirstFire).map(([b, ms]) => `${b}: first fire ${ms === null ? 'never' : `${(ms / 1000).toFixed(1)}s`}`),
    ];
    if (s.restartLatencies.length) {
      const ms = s.restartLatencies.map((r) => r.ms).sort((a, b) => a - b);
      const med = ms[Math.floor(ms.length / 2)];
      lines.push(`Restart latency: median ${med} ms, max ${ms[ms.length - 1]} ms over ${ms.length} restarts (target < 2000)`);
      const inp = s.restartLatencies.filter((r) => r.inputMs !== null).map((r) => r.inputMs as number).sort((a, b) => a - b);
      if (inp.length) lines.push(`Input → playable: median ${inp[Math.floor(inp.length / 2)]} ms over ${inp.length} tapped restarts`);
    }
    for (const l of Object.values(s.levels)) {
      lines.push(
        `${l.boardId}: attempts ${l.attempts}, ${l.passed ? `passed in ${(l.timeToPassMs! / 1000).toFixed(1)}s` : 'not passed'}${
          l.timeToAbandonMs !== null ? `, abandoned at ${(l.timeToAbandonMs / 1000).toFixed(1)}s` : ''
        }`,
      );
    }
    return lines.join('\n');
  }
}

export function loadStoredSessions(): Session[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session[]) : [];
  } catch {
    return [];
  }
}

export function clearStoredSessions(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
